import { Form, Formik } from 'formik';
import { FC, useState } from 'react';

import { SSOFacebook } from 'src/authentication/components/SSO/SSOFacebook';
import { SSOGoogle } from 'src/authentication/components/SSO/SSOGoogle';
import { AuthFormFooter } from 'src/authentication/sections/AuthFormFooter/AuthFormFooter';
import { AuthFormHeader } from 'src/authentication/sections/AuthFormHeader/AuthFormHeader';
import { AuthFormDisplayState } from 'src/authentication/templates/AuthForm/AuthForm.types';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { Divider } from 'src/general/components/Divider/Divider';
import { FormikOnErrorTracking } from 'src/general/components/forms/FormikOnErrorTracking/FormikOnErrorTracking';
import { getMicrocopy } from 'src/stores/microcopyStore';

import classes from './Forms.module.scss';
import { EmailFormValidation, EmailFormValues, FormProps } from './Forms.types';
import { InputEmailFormik } from 'src/general/components/forms/Input/InputEmailFormik';

interface Props extends FormProps<EmailFormValues> {
  displayState: AuthFormDisplayState;
  email?: string;
  redirectUrl?: string;
}

const initialValues: EmailFormValues = {
  email: '',
};

const microcopyKeys: { [key in AuthFormDisplayState]: { title: MICROCOPY; subtitle: MICROCOPY } } = {
  default: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_CAPTION,
  },
  watchlist: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_WATCHLIST_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_WATCHLIST_CAPTION,
  },
  carAlerts: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_CAR_ALERTS_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_CAR_ALERTS_CAPTION,
  },
  personalisedQuote: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_PERSONALISED_QUOTE_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_PERSONALISED_QUOTE_CAPTION,
  },
  checkout: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_CHECKOUT_SHORT_CAPTION,
  },
  carmatch: {
    title: MICROCOPY.AUTHENTICATION_SIGNUP_CARMATCH_TITLE,
    subtitle: MICROCOPY.AUTHENTICATION_SIGNUP_CARMATCH_CAPTION,
  },
};

export const EmailForm: FC<Props> = ({ displayState, onBack, onSubmit, onSubmitTracking, email, redirectUrl }) => {
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(false);
  const { title, subtitle } = microcopyKeys[displayState];
  const isCheckout = displayState === 'checkout';

  return (
    <>
      <AuthFormHeader title={getMicrocopy(title)} subtitle={getMicrocopy(subtitle, true)} onBack={onBack} />
      <div className={classes.emailForm}>
        <Formik
          initialValues={{ ...initialValues, email: email ?? initialValues.email }}
          onSubmit={onSubmit}
          validationSchema={EmailFormValidation}
          validateOnBlur={false}
          validateOnChange={validateAfterSubmit}
          validateOnMount={false}
        >
          {(props) => (
            <Form noValidate>
              {!!onSubmitTracking && <FormikOnErrorTracking onError={onSubmitTracking} />}
              <InputEmailFormik
                id="email"
                name="email"
                label="Email address"
                disabled={props.isSubmitting}
                defaultValue={email}
              />
              <Button
                type="submit"
                isLoading={props.isSubmitting}
                disabled={props.isSubmitting}
                fullWidth
                data-testid="submit"
                onClick={() => {
                  setValidateAfterSubmit(true);
                }}
              >
                Continue
              </Button>
            </Form>
          )}
        </Formik>
        <Divider className={classes.ssoDividerWrap} contentClassName={classes.ssoDivider}>
          OR
        </Divider>
        <div className={classes.ssoButtonsContainer}>
          <SSOGoogle redirectUrl={redirectUrl} />
          <SSOFacebook redirectUrl={redirectUrl} />
        </div>
      </div>
      <div className={classes.privacyAndTerms}>
        {getMicrocopy(MICROCOPY.AUTHENTICATION_SIGNUP_PRIVACY_AND_TERMS, true)}
      </div>
      {!isCheckout && <AuthFormFooter displayState={displayState} />}
    </>
  );
};
